import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Phone, ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { toast, Toaster } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const DEFAULT_STORE_BG =
    "https://customer-assets.emergentagent.com/job_prenota-stretch/artifacts/kjxcti1z_cms_image_desktop_20251015161300_OHewocbWQQ.webp";

function toYMD(d) {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}
function fmtItalianDate(d) {
    if (!d) return "";
    return d.toLocaleDateString("it-IT", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

export default function BookingPage() {
    const { slug } = useParams();
    const [store, setStore] = useState(null);
    const [storeProducts, setStoreProducts] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [step, setStep] = useState(1);
    const [month, setMonth] = useState(() => new Date());
    const [availability, setAvailability] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [loadingAvail, setLoadingAvail] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirmedBooking, setConfirmedBooking] = useState(null);
    const [utm, setUtm] = useState({});
    const [form, setForm] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        notes: "",
        privacy_consent: false,
        marketing_consent: false,
    });

    // Capture UTM params from URL (persisted across navigation via sessionStorage)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const captured = {
            utm_source: params.get("utm_source") || "",
            utm_medium: params.get("utm_medium") || "",
            utm_campaign: params.get("utm_campaign") || "",
            utm_content: params.get("utm_content") || "",
            utm_term: params.get("utm_term") || "",
        };
        const hasUtm = Object.values(captured).some(Boolean);
        if (hasUtm) {
            sessionStorage.setItem("magni_utm", JSON.stringify(captured));
            setUtm(captured);
        } else {
            try {
                const stored = sessionStorage.getItem("magni_utm");
                if (stored) setUtm(JSON.parse(stored));
            } catch (_) {
                /* noop */
            }
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/stores/by-slug/${slug}`);
                setStore(data);
                // Fetch products this store offers (filtered by the public product list to keep only active)
                if (Array.isArray(data.product_ids) && data.product_ids.length > 0) {
                    try {
                        const r = await api.get("/products");
                        const all = r.data || [];
                        const matched = all.filter((p) => data.product_ids.includes(p.id));
                        setStoreProducts(matched);
                    } catch (_) {
                        setStoreProducts([]);
                    }
                }
            } catch (e) {
                if (e.response?.status === 404) setNotFound(true);
            }
        })();
    }, [slug]);

    useEffect(() => {
        if (!store) return;
        const ym = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
        setLoadingAvail(true);
        api.get(`/stores/${slug}/availability`, { params: { month: ym } })
            .then((r) => setAvailability(r.data))
            .finally(() => setLoadingAvail(false));
    }, [store, slug, month]);

    const slotsForDate = useMemo(() => {
        if (!availability || !selectedDate) return [];
        const ymd = toYMD(selectedDate);
        const day = availability.days.find((d) => d.date === ymd);
        return day ? day.slots : [];
    }, [availability, selectedDate]);

    const disabledDays = useMemo(() => {
        if (!availability) return [];
        const fullyUnavailable = availability.days
            .filter((d) => !d.slots.some((s) => s.available))
            .map((d) => {
                const [y, m, dd] = d.date.split("-").map(Number);
                return new Date(y, m - 1, dd);
            });
        return fullyUnavailable;
    }, [availability]);

    const canSubmitForm =
        form.first_name.trim() &&
        form.last_name.trim() &&
        form.email.trim() &&
        form.phone.trim() &&
        form.privacy_consent;

    const submitBooking = async () => {
        if (!canSubmitForm || !selectedDate || !selectedTime) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/bookings", {
                store_slug: slug,
                slot_date: toYMD(selectedDate),
                slot_time: selectedTime,
                ...form,
                email: form.email.trim().toLowerCase(),
                ...utm,
                referrer: document.referrer || "",
            });
            setConfirmedBooking(data.booking);
            setStep(3);

            // GTM dataLayer event for marketing tracking
            try {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: "generate_lead",
                    store_name: data.booking?.store_name || store?.name,
                    appointment_date: data.booking?.slot_date,
                    appointment_time: data.booking?.slot_time,
                    booking_id: data.booking?.id,
                });
            } catch (_) {
                /* GTM not available, ignore */
            }
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || e.message;
            toast.error(msg);
            if (e.response?.status === 409) {
                // Slot taken — refresh availability
                const ym = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
                const r = await api.get(`/stores/${slug}/availability`, {
                    params: { month: ym },
                });
                setAvailability(r.data);
                setSelectedTime(null);
                setStep(1);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (notFound) {
        return (
            <div className="min-h-screen flex flex-col bg-magni-cream">
                <SiteHeader />
                <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-32 text-center">
                    <div className="overline mb-3">Errore 404</div>
                    <h1 className="font-serif-display text-4xl sm:text-5xl text-magni-navy mb-6">
                        Negozio non trovato
                    </h1>
                    <p className="text-magni-muted mb-8">
                        Il negozio che stai cercando non esiste o non è più disponibile.
                    </p>
                    <Link to="/" className="magni-btn-primary inline-flex" data-testid="back-home-btn">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Torna alla lista negozi
                    </Link>
                </main>
                <SiteFooter />
            </div>
        );
    }

    if (!store) {
        return (
            <div className="min-h-screen flex flex-col bg-magni-cream">
                <SiteHeader />
                <main className="flex-1 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-magni-navy" />
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-magni-cream">
            <SiteHeader />
            <Toaster position="top-center" richColors />

            {/* STORE HEADER */}
            <section
                data-testid="store-header"
                className="relative overflow-hidden border-b border-magni-light"
            >
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url('${store.header_image_url || DEFAULT_STORE_BG}')`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{ background: "rgba(23,72,96,0.78)" }}
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 text-white">
                    <Link
                        to="/"
                        data-testid="back-to-stores-link"
                        className="inline-flex items-center gap-2 text-sm opacity-75 hover:opacity-100 mb-4 transition-opacity"
                    >
                        <ArrowLeft className="w-4 h-4" /> Tutti i negozi
                    </Link>
                    <div className="overline mb-3" style={{ color: "#e8f1f5" }}>
                        MagniStretch® Trial · 45 min · gratuito
                    </div>
                    <h1
                        data-testid="store-name-heading"
                        className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-5 max-w-3xl"
                    >
                        {store.name}
                    </h1>
                    <div className="flex flex-wrap gap-6 text-sm">
                        <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-magni-navy" />
                            <span>
                                {store.address}, {store.city}
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-magni-navy" />
                            <a
                                href={`tel:${store.phone}`}
                                className="hover:text-magni-navy"
                                data-testid="store-phone-link"
                            >
                                {store.phone}
                            </a>
                        </div>
                        <a
                            data-testid="store-maps-link"
                            href={
                                store.google_maps_url ||
                                `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${store.name} ${store.address} ${store.city}`)}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-4 hover:opacity-80"
                        >
                            Apri in Google Maps
                        </a>
                        {store.support_phone && (
                            <div className="flex items-center gap-2 text-white/80">
                                <span className="text-xs uppercase tracking-[0.15em] font-semibold">Assistenza</span>
                                <a href={`tel:${store.support_phone}`} className="hover:text-white">
                                    {store.support_phone}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* MAP EMBED */}
            <section className="border-b border-magni-light" data-testid="store-map-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <iframe
                        title={`Mappa ${store.name}`}
                        src={`https://www.google.com/maps?q=${encodeURIComponent(`${store.name} ${store.address} ${store.city}`)}&output=embed`}
                        className="w-full h-64 sm:h-80 border border-magni-light"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            </section>

            {/* PRODUCTS available in store */}
            {storeProducts.length > 0 && (
                <section className="border-b border-magni-light bg-white" data-testid="store-products-section">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                        <div className="overline mb-3">In questo negozio puoi provare</div>
                        <div className="flex flex-wrap gap-2">
                            {storeProducts.map((p) => (
                                <span
                                    key={p.id}
                                    data-testid={`store-product-${p.id}`}
                                    className="inline-flex items-center px-4 py-2 border border-magni-navy text-magni-navy text-sm font-semibold tracking-wide"
                                    style={{ background: "#fff" }}
                                >
                                    {p.name}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* STEPPER */}
            <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-8">
                <div
                    className="flex items-center gap-3 sm:gap-6 mb-10 text-xs sm:text-sm"
                    data-testid="stepper"
                >
                    {[
                        { n: 1, label: "Data e orario" },
                        { n: 2, label: "I tuoi dati" },
                        { n: 3, label: "Conferma" },
                    ].map((s, i, arr) => (
                        <React.Fragment key={s.n}>
                            <div
                                className={`flex items-center gap-2 ${step >= s.n ? "text-magni-navy" : "text-magni-muted opacity-60"}`}
                                data-testid={`step-${s.n}`}
                            >
                                <span
                                    className={`w-7 h-7 flex items-center justify-center text-xs font-semibold ${step >= s.n ? "bg-magni-navy text-white" : "bg-magni-light"}`}
                                >
                                    {s.n}
                                </span>
                                <span className="uppercase tracking-[0.15em] font-semibold hidden sm:inline">
                                    {s.label}
                                </span>
                            </div>
                            {i < arr.length - 1 && (
                                <div className="flex-1 h-px bg-magni-light" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </section>

            {/* STEPS */}
            <section className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pb-20">
                {step === 1 && (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 magni-fade">
                        <div className="lg:col-span-7 bg-white border border-magni-light p-6 sm:p-8">
                            <div className="overline mb-2">Step 1</div>
                            <h2 className="font-serif-display text-2xl sm:text-3xl text-magni-navy mb-6">
                                Scegli una data
                            </h2>
                            <div className="magni-calendar">
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={(d) => {
                                        setSelectedDate(d || null);
                                        setSelectedTime(null);
                                    }}
                                    month={month}
                                    onMonthChange={setMonth}
                                    locale={undefined}
                                    weekStartsOn={1}
                                    fromDate={new Date()}
                                    disabled={disabledDays}
                                />
                            </div>
                            {loadingAvail && (
                                <div className="mt-4 flex items-center gap-2 text-sm text-magni-muted">
                                    <Loader2 className="w-4 h-4 animate-spin" /> Caricamento disponibilità...
                                </div>
                            )}
                        </div>

                        <div className="lg:col-span-5 bg-white border border-magni-light p-6 sm:p-8">
                            <div className="overline mb-2">Step 1 · orari</div>
                            <h2 className="font-serif-display text-2xl sm:text-3xl text-magni-navy mb-2">
                                Fasce orarie
                            </h2>
                            <p className="text-sm text-magni-muted mb-6">
                                {selectedDate
                                    ? fmtItalianDate(selectedDate)
                                    : "Seleziona una data dal calendario per vedere gli orari disponibili"}
                            </p>

                            {selectedDate && (
                                <div
                                    className="grid grid-cols-3 sm:grid-cols-4 gap-2"
                                    data-testid="time-slots"
                                >
                                    {slotsForDate.length === 0 ? (
                                        <div className="col-span-full text-sm text-magni-muted py-8 text-center">
                                            Nessun orario disponibile per questa data.
                                        </div>
                                    ) : (
                                        slotsForDate.map((s) => (
                                            <button
                                                key={s.time}
                                                data-testid={`slot-${s.time}`}
                                                disabled={!s.available}
                                                onClick={() => setSelectedTime(s.time)}
                                                className={`border p-3 text-sm font-medium transition-all ${
                                                    selectedTime === s.time
                                                        ? "bg-magni-navy text-white border-magni-navy"
                                                        : s.available
                                                          ? "border-magni-light text-magni-navy hover:border-magni-navy hover:bg-magni-celeste"
                                                          : "border-magni-light text-magni-muted opacity-40 cursor-not-allowed"
                                                }`}
                                            >
                                                {s.time}
                                            </button>
                                        ))
                                    )}
                                </div>
                            )}

                            <button
                                data-testid="next-step-1-btn"
                                disabled={!selectedDate || !selectedTime}
                                onClick={() => setStep(2)}
                                className="magni-btn-primary w-full mt-8"
                            >
                                Continua <ChevronRight className="w-4 h-4 ml-2" />
                            </button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="max-w-3xl mx-auto magni-fade">
                        <div className="bg-white border border-magni-light p-6 sm:p-10">
                            <div className="overline mb-2">Step 2</div>
                            <h2 className="font-serif-display text-2xl sm:text-3xl text-magni-navy mb-2">
                                I tuoi dati
                            </h2>
                            <p className="text-sm text-magni-muted mb-8">
                                Riceverai una mail di conferma con tutti i dettagli.
                            </p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                        Nome *
                                    </label>
                                    <input
                                        data-testid="input-first-name"
                                        className="magni-input"
                                        value={form.first_name}
                                        onChange={(e) =>
                                            setForm({ ...form, first_name: e.target.value })
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                        Cognome *
                                    </label>
                                    <input
                                        data-testid="input-last-name"
                                        className="magni-input"
                                        value={form.last_name}
                                        onChange={(e) =>
                                            setForm({ ...form, last_name: e.target.value })
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                        Email *
                                    </label>
                                    <input
                                        data-testid="input-email"
                                        type="email"
                                        className="magni-input"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({ ...form, email: e.target.value })
                                        }
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                        Telefono *
                                    </label>
                                    <input
                                        data-testid="input-phone"
                                        className="magni-input"
                                        value={form.phone}
                                        onChange={(e) =>
                                            setForm({ ...form, phone: e.target.value })
                                        }
                                    />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="text-xs uppercase tracking-[0.15em] font-semibold text-magni-navy block mb-2">
                                        Note / Esigenze particolari
                                    </label>
                                    <textarea
                                        data-testid="input-notes"
                                        rows={3}
                                        className="magni-input"
                                        placeholder="Es. ho dolori lombari, preferisco un materasso più rigido..."
                                        value={form.notes}
                                        onChange={(e) =>
                                            setForm({ ...form, notes: e.target.value })
                                        }
                                    />
                                </div>
                            </div>

                            <div className="mt-8 space-y-3 text-sm">
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        data-testid="privacy-consent"
                                        type="checkbox"
                                        className="mt-1 w-4 h-4 accent-[#174860]"
                                        checked={form.privacy_consent}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                privacy_consent: e.target.checked,
                                            })
                                        }
                                    />
                                    <span className="text-magni-muted">
                                        Acconsento al trattamento dei miei dati personali secondo
                                        l'informativa sulla privacy. <span className="text-magni-error">*</span>
                                    </span>
                                </label>
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        data-testid="marketing-consent"
                                        type="checkbox"
                                        className="mt-1 w-4 h-4 accent-[#174860]"
                                        checked={form.marketing_consent}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                marketing_consent: e.target.checked,
                                            })
                                        }
                                    />
                                    <span className="text-magni-muted">
                                        Desidero ricevere comunicazioni promozionali da Magniflex
                                        (facoltativo).
                                    </span>
                                </label>
                            </div>

                            <div className="flex items-center justify-between mt-10 gap-4">
                                <button
                                    data-testid="back-step-2-btn"
                                    className="magni-btn-ghost"
                                    onClick={() => setStep(1)}
                                >
                                    <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
                                </button>
                                <button
                                    data-testid="submit-booking-btn"
                                    className="magni-btn-primary"
                                    disabled={!canSubmitForm || submitting}
                                    onClick={submitBooking}
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Invio...
                                        </>
                                    ) : (
                                        <>Conferma prenotazione</>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Recap */}
                        <div className="mt-6 bg-magni-cream border border-magni-light p-5 text-sm flex flex-wrap gap-6 text-magni-muted">
                            <div>
                                <span className="overline block">Negozio</span>
                                {store.name}
                            </div>
                            <div>
                                <span className="overline block">Data</span>
                                {fmtItalianDate(selectedDate)}
                            </div>
                            <div>
                                <span className="overline block">Ora</span>
                                {selectedTime}
                            </div>
                        </div>
                    </div>
                )}

                {step === 3 && confirmedBooking && (
                    <div className="max-w-2xl mx-auto magni-fade text-center pt-6">
                        <div className="bg-white border border-magni-light p-10 sm:p-14">
                            <div
                                className="w-16 h-16 mx-auto bg-magni-navy text-white flex items-center justify-center mb-6"
                                style={{ borderRadius: 0 }}
                            >
                                <svg
                                    className="w-8 h-8"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            <div className="overline mb-3">Prenotazione confermata</div>
                            <h2 className="font-serif-display text-3xl sm:text-4xl text-magni-navy mb-4">
                                La tua prova MagniStretch® è prenotata!
                            </h2>
                            <p className="text-magni-muted mb-8">
                                Ti aspettiamo da <strong>{confirmedBooking.store_name}</strong>,
                                <br />
                                {fmtItalianDate(
                                    (() => {
                                        const [y, m, d] = confirmedBooking.slot_date
                                            .split("-")
                                            .map(Number);
                                        return new Date(y, m - 1, d);
                                    })(),
                                )}{" "}
                                alle {confirmedBooking.slot_time}.
                            </p>

                            <div className="bg-magni-cream border border-magni-light p-5 text-left text-sm space-y-2 mb-8">
                                <div>
                                    <span className="overline block">Cliente</span>
                                    {confirmedBooking.first_name} {confirmedBooking.last_name}
                                </div>
                                <div>
                                    <span className="overline block">Email</span>
                                    {confirmedBooking.email}
                                </div>
                                <div>
                                    <span className="overline block">Codice prenotazione</span>
                                    <code className="text-xs">{confirmedBooking.id}</code>
                                </div>
                            </div>

                            {(() => {
                                const [y, mo, d] = confirmedBooking.slot_date.split("-").map(Number);
                                const [hh, mm] = confirmedBooking.slot_time.split(":").map(Number);
                                const dur = confirmedBooking.slot_duration_minutes || 45;
                                const startLocal = new Date(y, mo - 1, d, hh, mm);
                                const endLocal = new Date(startLocal.getTime() + dur * 60000);
                                const toUTCStamp = (dt) => {
                                    const z = (n) => String(n).padStart(2, "0");
                                    return (
                                        dt.getUTCFullYear() +
                                        z(dt.getUTCMonth() + 1) +
                                        z(dt.getUTCDate()) +
                                        "T" +
                                        z(dt.getUTCHours()) +
                                        z(dt.getUTCMinutes()) +
                                        z(dt.getUTCSeconds()) +
                                        "Z"
                                    );
                                };
                                const startUTC = toUTCStamp(startLocal);
                                const endUTC = toUTCStamp(endLocal);
                                const title = "MagniStretch® Trial – Magniflex";
                                const loc = `${store.name} — ${store.address}, ${store.city}`;
                                const details = `Prova MagniStretch® presso ${store.name}. Telefono: ${store.phone}. Durata: ${dur} minuti.`;
                                const gcal = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${startUTC}/${endUTC}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(loc)}`;
                                const outlook = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(title)}&startdt=${encodeURIComponent(startLocal.toISOString())}&enddt=${encodeURIComponent(endLocal.toISOString())}&body=${encodeURIComponent(details)}&location=${encodeURIComponent(loc)}`;
                                const dest = `${store.address}, ${store.city}`;
                                const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
                                const amaps = `https://maps.apple.com/?daddr=${encodeURIComponent(dest)}`;
                                return (
                                    <>
                                        <div className="mb-6">
                                            <div className="overline mb-3">Aggiungi al calendario</div>
                                            <div className="flex flex-wrap gap-2 justify-center">
                                                <a
                                                    data-testid="cal-google-btn"
                                                    href={gcal}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="magni-btn-primary text-xs"
                                                >
                                                    Google Calendar
                                                </a>
                                                <a
                                                    data-testid="cal-apple-btn"
                                                    href={`${API}/bookings/${confirmedBooking.id}/ics`}
                                                    className="magni-btn-secondary text-xs"
                                                >
                                                    Apple Calendar
                                                </a>
                                                <a
                                                    data-testid="cal-outlook-btn"
                                                    href={outlook}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="magni-btn-secondary text-xs"
                                                >
                                                    Outlook
                                                </a>
                                            </div>
                                        </div>
                                        <div className="mb-8">
                                            <div className="overline mb-3">Come arrivare</div>
                                            <div className="flex flex-wrap gap-2 justify-center">
                                                <a
                                                    data-testid="dir-google-btn"
                                                    href={gmaps}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="magni-btn-primary text-xs"
                                                >
                                                    Indicazioni · Google Maps
                                                </a>
                                                <a
                                                    data-testid="dir-apple-btn"
                                                    href={amaps}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="magni-btn-secondary text-xs"
                                                >
                                                    Indicazioni · Apple Maps
                                                </a>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}

                            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                <a
                                    data-testid="ics-download-btn"
                                    href={`${API}/bookings/${confirmedBooking.id}/ics`}
                                    className="magni-btn-secondary"
                                >
                                    Scarica .ics
                                </a>
                                <Link to="/" className="magni-btn-secondary" data-testid="home-btn">
                                    Torna alla home
                                </Link>
                            </div>
                            <p className="text-xs text-magni-muted mt-6">
                                Una mail di conferma è stata inviata a {confirmedBooking.email}.
                            </p>
                            {store.support_phone && (
                                <p className="text-xs text-magni-muted mt-2">
                                    Hai bisogno di assistenza? Chiama il{" "}
                                    <a
                                        href={`tel:${store.support_phone}`}
                                        className="text-magni-navy underline underline-offset-2 font-semibold"
                                    >
                                        {store.support_phone}
                                    </a>
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </section>

            <SiteFooter />
        </div>
    );
}
